import {FC, useMemo} from 'react';
import {EditorIcon} from '~/common/FabricInvitation/EventCreateDesign/components/EditorIcons/EditorIcon';
import {MixedStickerAssetType} from '~/common/types/assets';
import {AssetIconType} from '~/common/types/assets_icons';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import './Stickers.sass';

interface StickerListProps {
  stickerIcons: MixedStickerAssetType[];
  showAll?: boolean;
  toggleShowAll?: () => void;
  isCustom?: boolean;
  onClickHandler: (icon: AssetIconType, id: string | number) => void;
}

const STICKERS_PER_ROW = 5;
const FIRST_ROW_LAST_STICKER_INDEX = STICKERS_PER_ROW - 1;

export const StickerList: FC<StickerListProps> = ({
  isCustom = false,
  stickerIcons,
  showAll,
  toggleShowAll,
  onClickHandler,
}) => {
  const viewAllButton = useMemo(
    () => (
      <Button
        data-qa-id="sticker-upload__view-all"
        className="sticker-upload__view-all"
        variant="transparent"
        onClick={toggleShowAll}
        title="Click to view all custom stickers"
      >
        <Typography variant="link" size="smaller" color="ghost">
          See all
        </Typography>
      </Button>
    ),
    [toggleShowAll]
  );

  const viewLessButton = useMemo(
    () => (
      <Button
        data-qa-id="sticker-upload__view-less"
        className="sticker-upload__view-less"
        variant="transparent"
        onClick={toggleShowAll}
        title="Click to view less custom stickers"
      >
        <Typography variant="link" size="smaller" className="sticker-upload__view-less--text">
          See less
        </Typography>
      </Button>
    ),
    [toggleShowAll]
  );

  if (isCustom) {
    // pick either the full list or just the first row
    const stickerIconsToDisplay = showAll ? stickerIcons : stickerIcons.slice(0, STICKERS_PER_ROW);

    if (stickerIconsToDisplay.length === 0) {
      return (
        <div className="sticker-upload-text-wrapper">
          <Typography variant="paragraph2" className="sticker-upload-text">
            Upload your own
          </Typography>
          <Typography variant="paragraph2" className="sticker-upload-subtext">
            Use any image or logo - we&#39;ll make it a sticker!
          </Typography>
        </div>
      );
    }

    return (
      <>
        {stickerIconsToDisplay.map((icon: MixedStickerAssetType, idx: number) => {
          // show the 'view all' button in the last slot of the first row
          if (
            !showAll &&
            idx === FIRST_ROW_LAST_STICKER_INDEX &&
            stickerIcons.length > STICKERS_PER_ROW
          ) {
            return (
              <div className="stickers__last-tile-contents" key={icon.id}>
                {viewAllButton}
                <EditorIcon
                  icon={icon.url}
                  id={icon.id}
                  onClick={onClickHandler}
                  hideAnimatedIndicator
                  height={40}
                  width={40}
                  isActive={false}
                  loading={idx < 8 ? 'eager' : 'lazy'}
                  cssCover={icon.cssCover}
                />
              </div>
            );
          }

          return (
            <EditorIcon
              key={icon.id}
              icon={icon.url}
              id={icon.id}
              onClick={onClickHandler}
              hideAnimatedIndicator
              height={40}
              width={40}
              isActive={false}
              loading={idx < 8 ? 'eager' : 'lazy'}
              cssCover={icon.cssCover}
            />
          );
        })}
        {showAll && viewLessButton}
      </>
    );
  }
  return stickerIcons?.map((icon: MixedStickerAssetType, idx: number) => (
    <EditorIcon
      key={icon.id}
      icon={icon.url}
      id={icon.id}
      onClick={onClickHandler}
      hideAnimatedIndicator
      height={40}
      width={40}
      isActive={false}
      loading={idx < 8 ? 'eager' : 'lazy'}
      cssCover={icon.cssCover}
    />
  ));
};
